import { DatasheetElement, DatasheetElementId } from "../types/datasheet-element";

type TabId = string | number;

export type DatasheetStorageTabs = Record<TabId, Record<DatasheetElementId, DatasheetElement>>;

type DatasheetElementsHistoryRecordId = string;
type TabHistory = {
  sequence: Array<DatasheetElementsHistoryRecordId>;
  elements: Record<DatasheetElementsHistoryRecordId, Record<DatasheetElementId, DatasheetElement>>;
  currentHistoryRecordId: DatasheetElementsHistoryRecordId;
};
type TabsHistory = Record<TabId, TabHistory>;

export class DatasheetStorage {
  private tabs: DatasheetStorageTabs = {};
  private history: TabsHistory = {};

  addTab(tabId: TabId) {
    this.tabs[tabId] = {};
  }

  removeTab(tabId: TabId) {
    delete this.tabs[tabId];
  }

  setElements(tabId: TabId, elements: Record<DatasheetElementId, DatasheetElement>) {
    this.tabs[tabId] = { ...elements };
  }

  updateElement(tabId: TabId, el: DatasheetElement) {
    this.tabs[tabId]![el.id] = el;
  }

  removeElement(tabId: TabId, el: DatasheetElement) {
    delete this.tabs[tabId]![el.id];
  }

  getElements(tabId: TabId) {
    return this.tabs[tabId];
  }

  updateHistoryRecord(
    params: {
      tabId: TabId;
      historyRecordId: DatasheetElementsHistoryRecordId;
      elements: Record<DatasheetElementId, DatasheetElement>
    }
  ) {
    const { tabId, historyRecordId, elements } = params;

    const tabHistory = this.history[tabId];

    if (tabHistory) {
      tabHistory.elements[historyRecordId] = elements;
    }
  }

  addHistoryRecord(
    params: {
      tabId: TabId,
      currentHistoryRecordId: string | undefined,
      newHistoryRecordId: string,
      elementsForNewHistoryRecord: Record<DatasheetElementId, DatasheetElement>,
    }
  ) {
    const { tabId, currentHistoryRecordId, newHistoryRecordId, elementsForNewHistoryRecord } = params;

    const tabHistory = this.history[tabId];

    if (tabHistory && currentHistoryRecordId) {
      const currentHistoryIndex = tabHistory.sequence.indexOf(currentHistoryRecordId);
      const historyRecordsToDelete = currentHistoryIndex >= 0 
        ? tabHistory.sequence.slice(currentHistoryIndex + 1)
        : [];

      historyRecordsToDelete.forEach((historyRecordId) => {
        delete tabHistory.elements[historyRecordId];
      });

      tabHistory.sequence = tabHistory.sequence
        .slice(0, currentHistoryIndex + 1)
        .concat(newHistoryRecordId);

      tabHistory.elements[newHistoryRecordId] = elementsForNewHistoryRecord;

      return;
    }

    this.history[tabId] = {
      sequence: [newHistoryRecordId],
      elements: {
        [newHistoryRecordId]: elementsForNewHistoryRecord,
      },
      currentHistoryRecordId: newHistoryRecordId,
    };
  }

  setCurrentHistoryRecordId(tabId: TabId, historyRecordId: DatasheetElementsHistoryRecordId) {
    const tabHistory = this.history[tabId];

    if (tabHistory) {
      tabHistory.currentHistoryRecordId = historyRecordId;
    }
  }

  getCurrentHistoryRecordId(tabId: TabId) {
    const tabHistory = this.history[tabId];

    return tabHistory?.currentHistoryRecordId;
  }

  getHistoryRecord(tabId: TabId, historyRecordId: DatasheetElementsHistoryRecordId) {
    const tabHistory = this.history[tabId];

    return tabHistory?.elements[historyRecordId];
  }

  getHistorySequence(tabId: TabId) {
    const tabHistory = this.history[tabId];

    return tabHistory?.sequence;
  }

  deleteHistory(tabId: TabId) {
    delete this.history[tabId];
  }
}